import client from './client';

export const PUBLIC_PATH = '/api/share-knowledge/';
export const ADMIN_PATH = '/api/admin/share-knowledge/';
export const DISPUTE_PATH = '/api/share-knowledge-disputes/';
export const ADMIN_DISPUTE_PATH = '/api/admin/share-knowledge-disputes/';

/** Build a multipart FormData payload for a share-knowledge submission. */
const toFormData = (payload) => {
  const fd = new FormData();
  const fields = ['name', 'email', 'mobile', 'title', 'category', 'content', 'status', 'is_published'];
  fields.forEach((key) => {
    if (payload[key] !== undefined && payload[key] !== null && payload[key] !== '') {
      fd.append(key, payload[key]);
    }
  });
  if (payload.proof_attachment instanceof File) {
    fd.append('proof_attachment', payload.proof_attachment, payload.proof_attachment.name);
  }
  if (payload.is_published !== undefined) fd.set('is_published', payload.is_published ? 'true' : 'false');
  return fd;
};

export const shareKnowledgeApi = {
  // Public endpoints
  list(params = {}) {
    return client.get(PUBLIC_PATH, { params }).then((r) => r.data);
  },

  // Distinct contributor names for the public author filter.
  authors() {
    return client.get(`${PUBLIC_PATH}authors/`).then((r) => r.data);
  },

  detail(id) {
    return client.get(`${PUBLIC_PATH}${id}/`).then((r) => r.data);
  },

  submit(payload, { onUploadProgress } = {}) {
    return client.post(PUBLIC_PATH, toFormData(payload), { timeout: 0, onUploadProgress }).then((r) => r.data);
  },

  like(id) {
    return client.post(`${PUBLIC_PATH}${id}/like/`).then((r) => r.data);
  },

  // Admin endpoints
  adminList(params = {}) {
    return client.get(ADMIN_PATH, { params }).then((r) => r.data);
  },

  adminDetail(id) {
    return client.get(`${ADMIN_PATH}${id}/`).then((r) => r.data);
  },

  adminUpdate(id, payload, { onUploadProgress } = {}) {
    return client.patch(`${ADMIN_PATH}${id}/`, toFormData(payload), { timeout: 0, onUploadProgress }).then((r) => r.data);
  },

  adminCreate(payload, { onUploadProgress } = {}) {
    return client.post(ADMIN_PATH, toFormData(payload), { timeout: 0, onUploadProgress }).then((r) => r.data);
  },

  adminRemove(id) {
    return client.delete(`${ADMIN_PATH}${id}/`).then((r) => r.data);
  },

  approve(id) {
    return client.post(`${ADMIN_PATH}${id}/approve/`).then((r) => r.data);
  },

  reject(id) {
    return client.post(`${ADMIN_PATH}${id}/reject/`).then((r) => r.data);
  },

  publishToSite(id) {
    return client.post(`${ADMIN_PATH}${id}/publish_to_site/`).then((r) => r.data);
  },

  /** Fetch the confidential proof as a blob (JWT cookie/header aware) and return a download URL. */
  async downloadProof(id) {
    const res = await client.get(`${ADMIN_PATH}${id}/proof/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const disposition = String(res.headers?.['content-disposition'] || '');
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return { url, filename: match ? match[1] : `proof-${id}` };
  },

  // Disputes
  submitDispute(payload, { onUploadProgress } = {}) {
    const fd = new FormData();
    const fields = ['name', 'email', 'mobile', 'description'];
    fields.forEach((key) => {
      if (payload[key] !== undefined && payload[key] !== null && payload[key] !== '') {
        fd.append(key, payload[key]);
      }
    });
    if (payload.share_knowledge) fd.append('share_knowledge', String(payload.share_knowledge));
    if (payload.attachment instanceof File) {
      fd.append('attachment', payload.attachment, payload.attachment.name);
    }
    return client.post(DISPUTE_PATH, fd, { timeout: 0, onUploadProgress }).then((r) => r.data);
  },

  adminDisputeList(params = {}) {
    return client.get(ADMIN_DISPUTE_PATH, { params }).then((r) => r.data);
  },

  adminDisputeRemove(id) {
    return client.delete(`${ADMIN_DISPUTE_PATH}${id}/`).then((r) => r.data);
  },

  /** Fetch a dispute's optional attachment as a blob and return a download URL. */
  async downloadDisputeAttachment(id) {
    const res = await client.get(`${ADMIN_DISPUTE_PATH}${id}/attachment/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const disposition = String(res.headers?.['content-disposition'] || '');
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return { url, filename: match ? match[1] : `dispute-${id}` };
  },
};